import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input } from '@iq/react-components';
import { HvTabs, HvTab } from '@hitachivantara/uikit-react-core';

import SelectPollingDateRange from '../../SelectPollingDateRange';
import { getActiveSite } from '../../../bundles/sites';
import { getPollingDateRange } from '../../../bundles/application';
import { fetchExportAlarms } from '../../../bundles/alarms';
import { getMappedUsers } from '../../../bundles/ad';
import { ALARM_TABS, buildAlarmQuery, filterAlarmsBySearch } from './utils';
import { exportAlarmsToFile } from '../../../utils';

const AlarmHeader = ({
  panelId,
  activeTab,
  onTabChange,
  searchText,
  onSearchChange,
  onRefresh,
  newAlarmsCount = 0,
  acknowledgedAlarmsCount = 0,
  resolvedAlarmsCount = 0,
  from,
  to,
  sortBy,
  sortOrder,
  deviatingReferenceDesignations,
  includeChildren,
  exportSearchText,
  canExport = true,
}) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const usersById = useSelector(getMappedUsers);
  const dateRange = useSelector((state) => getPollingDateRange(state, panelId));

  const tabCounts = {
    [ALARM_TABS.NEW]: newAlarmsCount,
    [ALARM_TABS.ACKNOWLEDGED]: acknowledgedAlarmsCount,
    [ALARM_TABS.RESOLVED]: resolvedAlarmsCount,
  };

  const tabIndexes = {
    [ALARM_TABS.NEW]: 0,
    [ALARM_TABS.ACKNOWLEDGED]: 1,
    [ALARM_TABS.RESOLVED]: 2,
  };

  const activeTabCount = tabCounts[activeTab] ?? 0;
  const activeTabIndex = tabIndexes[activeTab] ?? 0;

  const handleTabChange = useCallback(
    (_event, newTabIndex) => {
      const mapping = [ALARM_TABS.NEW, ALARM_TABS.ACKNOWLEDGED, ALARM_TABS.RESOLVED];
      const activeTab = mapping[newTabIndex] ?? ALARM_TABS.NEW;
      onTabChange(activeTab);
    },
    [onTabChange]
  );

  const handleExport = useCallback(() => {
    if (!canExport) {
      return;
    }

    const stateMap = {
      [ALARM_TABS.NEW]: 'new',
      [ALARM_TABS.ACKNOWLEDGED]: 'acknowledged',
      [ALARM_TABS.RESOLVED]: 'resolved',
    };

    const query = buildAlarmQuery({
      site,
      state: stateMap[activeTab],
      from,
      to,
      sortBy,
      sortOrder,
      deviatingReferenceDesignations,
      includeChildren,
    });

    dispatch(
      fetchExportAlarms({
        query,
        exportCallback: (alarms) => {
          exportAlarmsToFile({
            dateRange,
            siteName: site?.name || 'Unknown',
            exportType: 'xlsx',
            alarmState: stateMap[activeTab],
            alarms: filterAlarmsBySearch(alarms, exportSearchText),
            usersById,
            onExportCompleted: () => {
              console.log('Alarm export completed');
            },
          });
        },
      })
    );
  }, [
    activeTab,
    canExport,
    deviatingReferenceDesignations,
    dispatch,
    exportSearchText,
    from,
    includeChildren,
    site,
    sortBy,
    sortOrder,
    to,
    usersById,
    dateRange,
  ]);

  const exportDisabled = !canExport || activeTabCount === 0;
  const exportTooltip = exportDisabled ? 'No alarms to export' : 'Export alarms';

  return (
    <div className="alarm-panel-header">
      <div className="alarm-panel-toolbar">
        <div className="alarm-panel-filters">
          <Button
            icon={<Icon icon="he-download" />}
            activity="secondary"
            onClick={handleExport}
            disabled={exportDisabled}
            tooltip={exportTooltip}
          />
          <div className="alarm-tools-right">
            {panelId && (
              <div className="alarm-panel-polling-range">
                <SelectPollingDateRange
                  panelId={panelId}
                  enableGlobalDates={true}
                  onPoll={onRefresh}
                  // Section can be a fixed-height (1/2, 2/3) container that
                  // clips overflow - see escapeOverflow in SelectPollingDateRange.
                  escapeOverflow
                />
              </div>
            )}
            <div className="alarm-panel-search">
              <Input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={onSearchChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="alarm-panel-tabs">
          <HvTabs
            id="alarm-panel-tabs"
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <HvTab
              id="new-alarms"
              label={`New Alarms (${newAlarmsCount})`}
            />
            <HvTab
              id="acknowledged-alarms"
              label={`Acknowledged Alarms (${acknowledgedAlarmsCount})`}
            />
            <HvTab
              id="resolved-alarms"
              label={`Resolved Alarms (${resolvedAlarmsCount})`}
            />
          </HvTabs>
        </div>
      </div>
    </div>
  );
};

export default AlarmHeader;
